import { useEffect, useRef } from "react";
import { createChart, createSeriesMarkers, CandlestickSeries, LineSeries, HistogramSeries, CrosshairMode, LineStyle } from "lightweight-charts";
import { sma, ema, bollinger, rsi, macd, supportResistance } from "@/lib/indicators";

const UP = "#10B981", DOWN = "#EF4444", GOLD = "#F59E0B";

function buildChart(el, { dark, chartType, ind, decimals }) {
  const grid = dark ? "rgba(31,41,55,.6)" : "rgba(226,232,240,.8)";
  const chart = createChart(el, {
    autoSize: true,
    localization: { locale: "pt-BR" },
    layout: { background: { color: "transparent" }, textColor: dark ? "#9CA3AF" : "#475569", fontFamily: "JetBrains Mono", panes: { separatorColor: grid } },
    grid: { vertLines: { color: grid }, horzLines: { color: grid } },
    crosshair: { mode: CrosshairMode.Normal },
    timeScale: { timeVisible: true, secondsVisible: false, borderColor: grid },
    rightPriceScale: { borderColor: grid },
  });
  const priceFormat = { type: "price", precision: decimals, minMove: 1 / 10 ** decimals };
  const s = {};
  s.main = chartType === "candle"
    ? chart.addSeries(CandlestickSeries, { upColor: UP, downColor: DOWN, borderVisible: false, wickUpColor: UP, wickDownColor: DOWN, priceFormat })
    : chart.addSeries(LineSeries, { color: GOLD, lineWidth: 2, priceFormat });
  s.markers = createSeriesMarkers(s.main, []);
  const thin = { lineWidth: 1, priceLineVisible: false, lastValueVisible: false, priceFormat };
  if (ind.volume) {
    s.vol = chart.addSeries(HistogramSeries, { priceScaleId: "vol", priceFormat: { type: "volume" }, priceLineVisible: false, lastValueVisible: false });
    chart.priceScale("vol").applyOptions({ scaleMargins: { top: 0.82, bottom: 0 } });
  }
  if (ind.ma) {
    s.ma20 = chart.addSeries(LineSeries, { ...thin, color: "#38BDF8" });
    s.ema50 = chart.addSeries(LineSeries, { ...thin, color: "#A78BFA" });
  }
  if (ind.bb) {
    ["bbU", "bbM", "bbL"].forEach((k) => { s[k] = chart.addSeries(LineSeries, { ...thin, color: "#94A3B8", lineStyle: k === "bbM" ? LineStyle.Dotted : LineStyle.Dashed }); });
  }
  let pane = 1;
  if (ind.rsi) {
    s.rsi = chart.addSeries(LineSeries, { lineWidth: 1, color: GOLD, priceLineVisible: false }, pane++);
    [70, 30].forEach((p) => s.rsi.createPriceLine({ price: p, color: "#64748B", lineStyle: LineStyle.Dashed, lineWidth: 1, axisLabelVisible: false }));
  }
  if (ind.macd) {
    s.macdH = chart.addSeries(HistogramSeries, { priceLineVisible: false, lastValueVisible: false }, pane);
    s.macd = chart.addSeries(LineSeries, { lineWidth: 1, color: "#38BDF8", priceLineVisible: false, lastValueVisible: false }, pane);
    s.macdS = chart.addSeries(LineSeries, { lineWidth: 1, color: "#F97316", priceLineVisible: false, lastValueVisible: false }, pane++);
  }
  chart.panes().forEach((p, i) => { if (i > 0) p.setHeight(110); });
  return { chart, s };
}

function applyData(s, candles, chartType) {
  const t = candles.map((c) => c.time);
  const closes = candles.map((c) => c.close);
  const line = (arr, f = (v) => v) => arr.map((v, i) => (v == null || f(v) == null ? null : { time: t[i], value: f(v) })).filter(Boolean);
  s.main.setData(chartType === "candle" ? candles.map(({ time, open, high, low, close }) => ({ time, open, high, low, close })) : line(closes));
  s.vol?.setData(candles.map((c) => ({ time: c.time, value: c.volume || 0, color: c.close >= c.open ? "rgba(16,185,129,.35)" : "rgba(239,68,68,.35)" })));
  s.ma20?.setData(line(sma(closes, 20)));
  s.ema50?.setData(line(ema(closes, 50)));
  if (s.bbU) {
    const bb = bollinger(closes);
    s.bbU.setData(line(bb, (b) => b?.upper)); s.bbM.setData(line(bb, (b) => b?.mid)); s.bbL.setData(line(bb, (b) => b?.lower));
  }
  s.rsi?.setData(line(rsi(closes)));
  if (s.macd) {
    const m = macd(closes);
    s.macd.setData(line(m, (x) => x.macd)); s.macdS.setData(line(m, (x) => x.signal));
    s.macdH.setData(m.map((x, i) => (x.hist == null ? null : { time: t[i], value: x.hist, color: x.hist >= 0 ? "rgba(16,185,129,.6)" : "rgba(239,68,68,.6)" })).filter(Boolean));
  }
}

export const PriceChart = ({ candles, chartType, ind, decimals, theme, levels, markers, drawMode, drawings, onDraw, fitKey }) => {
  const el = useRef(null);
  const ref = useRef(null);
  const lines = useRef([]);
  const fitted = useRef("");
  const drawRef = useRef({ drawMode, onDraw });
  drawRef.current = { drawMode, onDraw };
  const indKey = JSON.stringify(ind);

  useEffect(() => {
    const built = buildChart(el.current, { dark: theme === "dark", chartType, ind: JSON.parse(indKey), decimals });
    built.chart.subscribeClick((p) => {
      if (!drawRef.current.drawMode || !p.point) return;
      const price = built.s.main.coordinateToPrice(p.point.y);
      if (price != null) drawRef.current.onDraw(price);
    });
    ref.current = built;
    fitted.current = "";
    lines.current = [];
    return () => { built.chart.remove(); ref.current = null; };
  }, [chartType, indKey, theme, decimals]);

  useEffect(() => {
    if (!ref.current || !candles.length) return;
    applyData(ref.current.s, candles, chartType);
    if (fitted.current !== fitKey) {
      ref.current.chart.timeScale().fitContent();
      fitted.current = fitKey;
    }
  }, [candles, chartType, indKey, theme, decimals, fitKey]);

  const sr = ind.sr ? supportResistance(candles) : null;
  const linesKey = JSON.stringify({ levels, drawings, sr, markers });
  useEffect(() => {
    if (!ref.current) return;
    const { main, markers: mk } = ref.current.s;
    lines.current.forEach((l) => { try { main.removePriceLine(l); } catch { /* série recriada */ } });
    const all = [...(levels || [])];
    (drawings || []).forEach((p, i) => all.push({ price: p, color: "#E2E8F0", title: `Linha ${i + 1}`, style: LineStyle.Solid }));
    if (sr) {
      all.push({ price: sr.support, color: UP, title: "Suporte", style: LineStyle.LargeDashed });
      all.push({ price: sr.resistance, color: DOWN, title: "Resistência", style: LineStyle.LargeDashed });
    }
    lines.current = all.map((l) => main.createPriceLine({ price: l.price, color: l.color, title: l.title, lineWidth: 1, lineStyle: l.style ?? LineStyle.Dashed, axisLabelVisible: true }));
    mk.setMarkers(markers || []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linesKey, chartType, indKey, theme, decimals]);

  return <div ref={el} className={`h-full w-full ${drawMode ? "cursor-crosshair" : ""}`} data-testid="price-chart" />;
};
